/**
 * The task-based sidebar from the mockup (screen 01): Manage → Manager
 * overview, Launch follow-up, Content library (formats), Audience & needs,
 * Distribution, Interventions, Data quality. Every page in shared/catalog.js
 * appears here once (a test checks it), so the classic dashboard → tab list
 * under "Existing reports" is an alternative view, never the only way in.
 *
 * Entries:
 *   { key, label?, icon?, page: "dashboard/tab" }            a page
 *   { key, label, icon?, page?, children: [...], alwaysOpen? } a group; `page` is its own landing
 *   { key, label, icon, soon: "why" }                         planned, not built yet (shown disabled)
 * A page's label defaults to its tab title (tenant-specific names apply).
 */

import { findTab } from "../../shared/catalog.js";

export const NAV = [
  { key: "overview", label: "Manager overview", icon: "grid", page: "manage/overview" },
  { key: "launch", label: "Launch follow-up", icon: "launch", page: "manage/launch" },
  {
    key: "library",
    label: "Content library",
    icon: "book",
    page: "engagement/overview",
    alwaysOpen: true,
    children: [
      { key: "medwiki", page: "engagement/medwiki" },
      {
        key: "videos",
        label: "Video",
        children: [
          { key: "video", page: "engagement/video" },
          { key: "reels", page: "engagement/reels" },
        ],
      },
      { key: "series", page: "engagement/series" },
      { key: "webinar", page: "engagement/webinar" },
      { key: "lms", page: "engagement/lms" },
      {
        key: "other",
        label: "Other formats",
        children: [
          { key: "surveys", page: "engagement/surveys" },
          { key: "epub", page: "engagement/epub" },
          { key: "channels", page: "engagement/channels" },
        ],
      },
    ],
  },
  {
    key: "audience",
    label: "Audience & needs",
    icon: "users",
    children: [
      { key: "behaviour", page: "engagement/behaviour" },
      { key: "speciality", page: "engagement/speciality" },
    ],
  },
  {
    key: "distribution",
    label: "Distribution",
    icon: "send",
    children: [
      { key: "funnel", label: "Campaigns & funnel", page: "acquisition/funnel" },
      { key: "acq-overview", label: "Reach overview", page: "acquisition/overview" },
      { key: "web", page: "acquisition/web" },
      { key: "app", page: "acquisition/app" },
    ],
  },
  { key: "interventions", label: "Interventions", icon: "clipboard", soon: "Not built yet: assigning an owner, a hypothesis and a recheck to an item (plan B1)." },
  { key: "quality", label: "Data quality", icon: "shield", page: "monitoring/unusual" },
];

/** The catalog tab behind a "dashboard/tab" page key. */
export function tabOf(page) {
  const [dashboard, tab] = page.split("/");
  return findTab(dashboard, tab);
}

/** Every page key in a nav tree, depth first. */
export function navPages(entries = NAV) {
  return entries.flatMap((e) => [...(e.page ? [e.page] : []), ...navPages(e.children ?? [])]);
}

/**
 * The tree the user sees: pages they may open in this tenant (`allowed(tab)`),
 * and groups that still have one. Planned entries stay (shown disabled).
 */
export function visibleNav(allowed, entries = NAV) {
  return entries
    .map((e) => {
      if (e.soon) return e;
      const children = visibleNav(allowed, e.children ?? []);
      const own = e.page && tabOf(e.page) && allowed(tabOf(e.page)) ? e.page : null;
      if (!own && !children.length) return null;
      return { ...e, page: own, children };
    })
    .filter(Boolean);
}

/** True when the entry or one of its descendants is the current page. */
export function containsPage(entry, page) {
  return entry.page === page || (entry.children ?? []).some((c) => containsPage(c, page));
}

/** Where a click on an entry goes: its own page, else its first reachable descendant. */
export function landingOf(entry) {
  if (entry.page) return entry.page;
  for (const c of entry.children ?? []) {
    const p = landingOf(c);
    if (p) return p;
  }
  return null;
}
